import {
  first_truthy,
  normalize_text,
  submit_requestor_form_answers,
} from './request_portal_helpers';

function fact_key(fact = {}) {
  return String(fact?.section_question_id || fact?.question_id || fact?.label);
}

function editing_answer_payload(fact, answer_text) {
  return {
    section_question_id: Number(fact?.section_question_id),
    question_id: Number(fact?.question_id || 0) || null,
    answer_text,
    label: fact?.label,
  };
}

function edit_error_message(error) {
  return first_truthy([
    error?.response?.data?.message,
    error?.response?.data?.data?.message,
    error?.message,
    'Failed to save this section.',
  ]);
}

function first_empty_answer(answers = []) {
  return answers.find((answer) => !normalize_text(answer.answer_text));
}

function fact_has_visible_value(fact = {}) {
  return Boolean(
    (Array.isArray(fact?.values) && fact.values.some((value) => normalize_text(value)))
    || normalize_text(fact?.value)
  );
}

function case_item_key(case_item = {}) {
  return first_truthy([case_item?.id, case_item?.application_id, 'case']);
}

function answer_section_key(answer_section = {}) {
  return first_truthy([answer_section?.section_key, answer_section?.id, answer_section?.name, 'section']);
}

function risk_submission_answer(fact = {}) {
  const answer_text = Array.isArray(fact?.values)
    ? fact.values.map(normalize_text).filter(Boolean).join(', ')
    : normalize_text(fact?.value);

  if (!answer_text) {
    return null;
  }

  return {
    section_question_id: Number(fact?.section_question_id || 0) || null,
    question_id: Number(fact?.question_id || 0) || null,
    question_text: fact?.label || '',
    answer_text,
  };
}

function flattened_case_answers(case_item = {}) {
  return (Array.isArray(case_item?.case_fact_sections) ? case_item.case_fact_sections : [])
    .flatMap((section) => Array.isArray(section?.facts) ? section.facts : [])
    .map(risk_submission_answer)
    .filter(Boolean);
}

function case_section_key_value(case_item = {}, answer_section = {}) {
  return String(`${case_item_key(case_item)}-${answer_section_key(answer_section)}`);
}

export default {
    case_section_key(case_item = {}, answer_section = {}) {
      return case_section_key_value(case_item, answer_section);
    },
    can_edit_case_fact(fact = {}) {
      return Boolean(fact?.application_form_id && fact?.section_question_id && !fact?.document);
    },
    editable_case_section_facts(answer_section = {}) {
      return this.answer_section_facts(answer_section).filter((fact) => this.can_edit_case_fact(fact));
    },
    can_edit_answer_section(answer_section = {}) {
      return this.editable_case_section_facts(answer_section).length > 0;
    },
    case_section_has_answers(answer_section = {}) {
      return this.answer_section_facts(answer_section).some((fact) => fact_has_visible_value(fact));
    },
    should_prompt_case_section_completion(answer_section = {}) {
      return this.can_edit_answer_section(answer_section) && !this.case_section_has_answers(answer_section);
    },
    is_editing_case_section(case_item = {}, answer_section = {}) {
      return this.editing_case_section_key === this.case_section_key(case_item, answer_section);
    },
    is_saving_case_section(case_item = {}, answer_section = {}) {
      return this.saving_case_section_key === this.case_section_key(case_item, answer_section);
    },
    begin_case_section_edit(case_item = {}, answer_section = {}) {
      if (!this.can_edit_answer_section(answer_section)) {
        return;
      }

      this.editing_case_section_key = this.case_section_key(case_item, answer_section);
      this.editing_case_answers = this.editable_case_section_facts(answer_section).map((fact) => ({
        key: fact_key(fact),
        value: normalize_text(fact?.value),
        values: Array.isArray(fact?.values) ? [...fact.values] : [],
      }));
      this.case_section_edit_error = '';
      this.case_section_edit_success = '';
    },
    maybe_begin_empty_case_section(case_item = {}) {
      if (this.editing_case_section_key || !case_item) {
        return;
      }

      const empty_editable_section = (Array.isArray(case_item.case_fact_sections) ? case_item.case_fact_sections : [])
        .find((section) => this.should_prompt_case_section_completion(section));

      if (empty_editable_section) {
        this.begin_case_section_edit(case_item, empty_editable_section);
      }
    },
    cancel_case_section_edit() {
      this.editing_case_section_key = '';
      this.editing_case_answers = [];
      this.saving_case_section_key = '';
      this.case_section_edit_error = '';
    },
    editing_case_fact_record(fact = {}) {
      return this.editing_case_answers.find((record) => record.key === fact_key(fact)) || null;
    },
    editing_case_fact_value(fact = {}) {
      return this.editing_case_fact_record(fact)?.value || '';
    },
    editing_case_fact_values(fact = {}) {
      return this.editing_case_fact_record(fact)?.values || [];
    },
    update_case_fact_value(fact = {}, value = '') {
      const key = fact_key(fact);
      this.editing_case_answers = this.editing_case_answers.map((record) =>
        record.key === key ? { ...record, value } : record
      );
    },
    update_case_fact_multi_value(fact = {}, selected_options) {
      const key = fact_key(fact);
      const values = Array.from(selected_options || []).map((option) => option.value);
      this.editing_case_answers = this.editing_case_answers.map((record) =>
        record.key === key ? { ...record, values } : record
      );
    },
    update_case_fact_checkbox_value(fact = {}, option_value, is_checked) {
      const key = fact_key(fact);
      const normalized_option = normalize_text(option_value);
      const current_values = this.editing_case_fact_values(fact);
      const values = is_checked
        ? [...new Set([...current_values, normalized_option])]
        : current_values.filter((value) => value !== normalized_option);
      this.editing_case_answers = this.editing_case_answers.map((record) =>
        record.key === key ? { ...record, values } : record
      );
    },
    is_select_case_fact(fact = {}) {
      return normalize_text(fact?.question_type).includes('select')
        && !this.is_multi_select_case_fact(fact);
    },
    is_multi_select_case_fact(fact = {}) {
      return normalize_text(fact?.question_type).replace(/\s+/g, '_').includes('multi_select');
    },
    is_checkbox_case_fact(fact = {}) {
      return normalize_text(fact?.question_type).includes('checkbox');
    },
    is_radio_case_fact(fact = {}) {
      return normalize_text(fact?.question_type).includes('radio');
    },
    is_long_text_case_fact(fact = {}) {
      const question_type = normalize_text(fact?.question_type);
      return question_type.includes('long') || question_type.includes('text_area') || question_type.includes('textarea');
    },
    input_type_for_case_fact(fact = {}) {
      return input_type_for_fact(fact);
    },
    serialized_case_fact_answer(fact = {}) {
      const serializers = [
        { matches: is_multi_value_fact(this, fact), serialize: () => serialized_multi_value_answer(this, fact) },
        { matches: true, serialize: () => normalize_text(this.editing_case_fact_value(fact)) },
      ];
      return serializers.find((serializer) => serializer.matches).serialize();
    },
    case_section_edit_answers(editable_facts = []) {
      return editable_facts
        .map((fact) => editing_answer_payload(fact, this.serialized_case_fact_answer(fact)))
        .filter((answer) => answer.section_question_id);
    },
    async save_case_section_edit(case_item = {}, answer_section = {}) {
      const editable_facts = this.editable_case_section_facts(answer_section);
      const answers = this.case_section_edit_answers(editable_facts);
      const empty_answer = first_empty_answer(answers);

      if (!editable_facts.length) return;
      if (empty_answer) {
        this.case_section_edit_error = `Please enter an answer for ${empty_answer.label || 'all fields'} before saving.`;
        return;
      }

      this.saving_case_section_key = this.case_section_key(case_item, answer_section);
      this.case_section_edit_error = '';
      this.case_section_edit_success = '';

      try {
        await submit_requestor_form_answers({
          application_form_id: Number(editable_facts[0].application_form_id || case_item?.application_form_id),
          application_id: Number(case_item?.application_id),
          answers,
        });

        this.cancel_case_section_edit();
        await this.load_open_cases();
        const refreshed_case = this.backend_open_cases.find((item) =>
          Number(item?.id) === Number(case_item?.id)
          || Number(item?.application_id) === Number(case_item?.application_id)
        );

        if (refreshed_case?.entity_profile_id && refreshed_case?.entity_type_id) {
          await this.submit_case_risk_answers({
            entity_profile_id: refreshed_case.entity_profile_id,
            entity_type_id: refreshed_case.entity_type_id,
            submitted_answers: flattened_case_answers(refreshed_case),
          });
        }

        this.case_section_edit_success = `${answer_section?.name || 'Section'} updated.`;
      } catch (error) {
        this.case_section_edit_error = edit_error_message(error);
      } finally {
        this.saving_case_section_key = '';
      }
    },
};

function is_multi_value_fact(context, fact = {}) {
  return [context.is_multi_select_case_fact(fact), context.is_checkbox_case_fact(fact)].some(Boolean);
}

function serialized_multi_value_answer(context, fact = {}) {
  return context.editing_case_fact_values(fact).map(normalize_text).filter(Boolean).join(', ');
}

function case_fact_input_types() {
  return [
    { token: 'date', input_type: 'date' },
    { token: 'number', input_type: 'number' },
    { token: 'email', input_type: 'email' },
    { token: 'phone', input_type: 'tel' },
  ];
}

function input_type_for_fact(fact = {}) {
  const question_type = normalize_text(fact?.question_type);
  const matched_type = case_fact_input_types().find((item) => matches_input_type(question_type, item));
  return first_truthy([matched_type?.input_type, 'text']);
}

function matches_input_type(question_type = '', item = {}) {
  return question_type.includes(item.token);
}
